/**
 * Financial Calculation Engine
 * 
 * Excel-like formula engine for cascading financial calculations.
 * All changes propagate forward through subsequent years.
 */

import {
    FinancialProjectionModel,
    YearlyFinancials,
    FiscalYear,
    Scenario,
    parseFiscalYear,
    toFiscalYear,
    nextFiscalYear,
    generateFiscalYearRange,
    createEmptyYearlyFinancials,
    createCalculatedMetadata,
    createAIMetadata,
    InitiativeYearlyImpact
} from '../schemas/financialProjection';

// ============================================================================
// FORMULA DEFINITIONS
// ============================================================================

/**
 * Calculate organic revenue growth for a year
 * Formula: Total[Y-1] × OrganicGrowthRate[Y]
 */
export function calculateOrganicRevenueGrowth(
    priorYearTotal: number,
    organicGrowthRate: number
): number {
    return priorYearTotal * organicGrowthRate;
}

/**
 * Calculate strategy impact for a year
 * Formula: SUM(Initiative.RevenueImpact[Y])
 */
export function calculateStrategyRevenueImpact(
    initiatives: InitiativeYearlyImpact[] | undefined | null
): number {
    if (!initiatives || !Array.isArray(initiatives)) return 0;
    return initiatives.reduce((sum, init) => sum + (init.revenueImpact || 0), 0);
}

/**
 * Calculate total revenue for a year
 * Formula: Total[Y-1] + OrganicGrowth[Y] + StrategyImpact[Y]
 */
export function calculateRevenueTotal(
    priorYearTotal: number,
    organicGrowth: number,
    strategyImpact: number
): number {
    return priorYearTotal + organicGrowth + strategyImpact;
}

/**
 * Calculate gap to target
 * Formula: BusinessPlanTarget[Y] - Total[Y]
 * Negative = exceeded target
 */
export function calculateGapToTarget(
    businessPlanTarget: number,
    total: number
): number {
    return businessPlanTarget - total;
}

/**
 * Calculate organic EBITDA growth
 * Formula: OrganicRevenueGrowth[Y] × EBITDAMargin[Y]
 * 
 * Rationale: The incremental organic revenue generated this year contributes
 * EBITDA at this year's margin rate. This is the standard bridge approach:
 * each source of revenue change (organic, strategic) maps to an EBITDA
 * contribution independently, rather than implying a total and diffing.
 */
export function calculateOrganicEbitdaGrowth(
    organicRevenueGrowth: number,
    ebitdaMargin: number
): number {
    return organicRevenueGrowth * ebitdaMargin;
}

/**
 * Calculate gross strategy EBITDA impact
 * Formula: SUM(Initiative.grossEbitdaImpact[Y])
 * Falls back to SUM(Initiative.revenueImpact[Y] × EBITDAMargin[Y]) if
 * grossEbitdaImpact is not set.
 * 
 * If the user has manually edited an initiative's grossEbitdaImpact, that
 * value takes precedence over the derived revenue × margin calculation.
 */
export function calculateGrossStrategyEbitdaImpact(
    initiatives: InitiativeYearlyImpact[] | undefined | null,
    ebitdaMargin: number
): number {
    if (!initiatives || !Array.isArray(initiatives)) return 0;
    return initiatives.reduce((sum, init) => {
        // Prefer user-edited grossEbitdaImpact; fall back to revenue × margin
        if (init.grossEbitdaImpactMetadata?.source === 'user_edited' && init.grossEbitdaImpact != null) {
            return sum + init.grossEbitdaImpact;
        }
        return sum + (init.revenueImpact || 0) * ebitdaMargin;
    }, 0);
}

/**
 * Calculate total CAPEX for a year
 */
export function calculateTotalCapex(
    initiatives: InitiativeYearlyImpact[] | undefined | null
): number {
    if (!initiatives || !Array.isArray(initiatives)) return 0;
    return initiatives.reduce((sum, init) => sum + (init.capex || 0), 0);
}

/**
 * Calculate total OPEX for a year
 */
export function calculateTotalOpex(
    initiatives: InitiativeYearlyImpact[] | undefined | null
): number {
    if (!initiatives || !Array.isArray(initiatives)) return 0;
    return initiatives.reduce((sum, init) => sum + (init.opex || 0), 0);
}

/**
 * Calculate total EBITDA
 * Formula: TotalRevenue[Y] × EBITDAMargin[Y]
 * 
 * The margin applies equally to all revenue (base + organic + strategic).
 * The bridge breakdown (BOY EBITDA, organic EBITDA, strategy EBITDA) is just
 * a decomposition: each equals the respective revenue component × margin.
 */
export function calculateEbitdaTotal(
    totalRevenue: number,
    ebitdaMargin: number
): number {
    return totalRevenue * ebitdaMargin;
}

/**
 * Calculate net cash position
 * Formula: TotalEBITDA - CAPEX - OPEX
 */
export function calculateNetCashPosition(
    totalEbitda: number,
    totalCapex: number,
    totalOpex: number
): number {
    return totalEbitda - totalCapex - totalOpex;
}

// ============================================================================
// YEAR CALCULATION
// ============================================================================

/**
 * Recalculate a single year's financials based on prior year data
 */
export function recalculateYear(
    currentYear: YearlyFinancials,
    priorYear: YearlyFinancials | null,
    preserveUserEdits: boolean = true
): YearlyFinancials {
    const isBaseYear = currentYear.isBaseYear;
    
    // For base year, only recalculate gap
    if (isBaseYear) {
        const gapToTarget = calculateGapToTarget(
            currentYear.revenue.businessPlanTarget,
            currentYear.revenue.total
        );
        
        return {
            ...currentYear,
            revenue: {
                ...currentYear.revenue,
                gapToTarget,
                gapToTargetMetadata: createCalculatedMetadata(
                    `${currentYear.revenue.businessPlanTarget} - ${currentYear.revenue.total}`
                )
            },
            ebitda: {
                ...currentYear.ebitda,
                gapToTarget: calculateGapToTarget(
                    currentYear.ebitda.businessPlanTarget,
                    currentYear.ebitda.totalEbitda
                ),
                gapToTargetMetadata: createCalculatedMetadata(
                    `${currentYear.ebitda.businessPlanTarget} - ${currentYear.ebitda.totalEbitda}`
                )
            }
        };
    }
    
    // For projected years, calculate everything
    const priorRevenueTotal = priorYear?.revenue.total || 0;
    
    const organicGrowthRate = currentYear.assumptions.organicGrowthRate;
    const ebitdaMargin = currentYear.assumptions.ebitdaMarginPercent;
    
    // ================================================================
    // REVENUE CALCULATIONS
    // ================================================================
    const organicRevenueGrowth = calculateOrganicRevenueGrowth(priorRevenueTotal, organicGrowthRate);
    const strategyRevenueImpact = calculateStrategyRevenueImpact(currentYear.revenue.initiativeBreakdown);
    const revenueTotal = calculateRevenueTotal(priorRevenueTotal, organicRevenueGrowth, strategyRevenueImpact);
    const revenueGap = calculateGapToTarget(currentYear.revenue.businessPlanTarget, revenueTotal);
    
    // ================================================================
    // EBITDA CALCULATIONS — Margin applies to ALL revenue equally
    // Total EBITDA = Total Revenue × EBITDA Margin
    // Bridge: BOY EBITDA + Organic EBITDA + Strategy EBITDA = Total
    // where each component = respective revenue component × margin
    // ================================================================
    
    // Auto-derive EBITDA BP target from Revenue BP Target × EBITDA Margin
    // UNLESS the user has explicitly edited the EBITDA BP target
    let ebitdaBPTarget = currentYear.ebitda.businessPlanTarget;
    if (currentYear.ebitda.businessPlanTargetMetadata?.source !== 'user_edited') {
        const derivedTarget = currentYear.revenue.businessPlanTarget * ebitdaMargin;
        if (derivedTarget > 0) {
            ebitdaBPTarget = derivedTarget;
        }
    }
    
    // BOY EBITDA = Prior Year Revenue × Current Year Margin
    // (applying current margin to the entire base revenue, not carrying forward prior EBITDA)
    const boyEbitda = priorRevenueTotal * ebitdaMargin;
    
    // Organic EBITDA Growth = Organic Revenue Growth × EBITDA Margin
    const organicEbitdaGrowth = calculateOrganicEbitdaGrowth(organicRevenueGrowth, ebitdaMargin);
    
    // Strategy EBITDA Impact — uses user-edited values if available, else revenue × margin
    const grossStrategyImpact = calculateGrossStrategyEbitdaImpact(
        currentYear.ebitda.initiativeBreakdown,
        ebitdaMargin
    );
    
    const totalCapex = calculateTotalCapex(currentYear.ebitda.initiativeBreakdown);
    const totalOpex = calculateTotalOpex(currentYear.ebitda.initiativeBreakdown);
    
    // Total EBITDA = Total Revenue × Margin (this equals BOY + Organic + Strategy)
    const ebitdaTotal = calculateEbitdaTotal(revenueTotal, ebitdaMargin);
    
    const netCashPosition = calculateNetCashPosition(ebitdaTotal, totalCapex, totalOpex);
    const ebitdaGap = calculateGapToTarget(ebitdaBPTarget, ebitdaTotal);
    
    return {
        ...currentYear,
        revenue: {
            ...currentYear.revenue,
            organicGrowth: organicRevenueGrowth,
            organicGrowthMetadata: createCalculatedMetadata(
                `${priorRevenueTotal.toFixed(2)} × ${(organicGrowthRate * 100).toFixed(1)}%`
            ),
            strategyImpact: strategyRevenueImpact,
            strategyImpactMetadata: createCalculatedMetadata(
                `SUM(Initiative Revenue Impacts)`
            ),
            total: revenueTotal,
            totalMetadata: createCalculatedMetadata(
                `${priorRevenueTotal.toFixed(2)} + ${organicRevenueGrowth.toFixed(2)} + ${strategyRevenueImpact.toFixed(2)}`
            ),
            gapToTarget: revenueGap,
            gapToTargetMetadata: createCalculatedMetadata(
                `${currentYear.revenue.businessPlanTarget.toFixed(2)} - ${revenueTotal.toFixed(2)}`
            )
        },
        ebitda: {
            ...currentYear.ebitda,
            businessPlanTarget: ebitdaBPTarget,
            businessPlanTargetMetadata: currentYear.ebitda.businessPlanTargetMetadata?.source === 'user_edited'
                ? currentYear.ebitda.businessPlanTargetMetadata
                : createCalculatedMetadata(
                    `${currentYear.revenue.businessPlanTarget.toFixed(2)} × ${(ebitdaMargin * 100).toFixed(1)}%`
                ),
            organicGrowth: organicEbitdaGrowth,
            organicGrowthMetadata: createCalculatedMetadata(
                `Organic Rev Growth (${organicRevenueGrowth.toFixed(2)}) × EBITDA Margin (${(ebitdaMargin * 100).toFixed(1)}%)`
            ),
            grossStrategyImpact,
            grossStrategyImpactMetadata: createCalculatedMetadata(
                `SUM(Initiative Revenue Impact × EBITDA Margin)`
            ),
            totalCapex,
            totalCapexMetadata: createCalculatedMetadata(`SUM(Initiative CAPEX)`),
            totalOpex,
            totalOpexMetadata: createCalculatedMetadata(`SUM(Initiative OPEX)`),
            totalEbitda: ebitdaTotal,
            totalEbitdaMetadata: createCalculatedMetadata(
                `Total Revenue (${revenueTotal.toFixed(2)}) × EBITDA Margin (${(ebitdaMargin * 100).toFixed(1)}%)`
            ),
            netCashPosition,
            netCashPositionMetadata: createCalculatedMetadata(
                `${ebitdaTotal.toFixed(2)} - ${totalCapex.toFixed(2)} - ${totalOpex.toFixed(2)}`
            ),
            gapToTarget: ebitdaGap,
            gapToTargetMetadata: createCalculatedMetadata(
                `${ebitdaBPTarget.toFixed(2)} - ${ebitdaTotal.toFixed(2)}`
            )
        }
    };
}

// ============================================================================
// CASCADE RECALCULATION
// ============================================================================

/**
 * Recalculate all years from a given starting year forward
 * This is the main cascade function - changes propagate forward
 */
export function recalculateFromYear(
    projections: YearlyFinancials[],
    changedYear: FiscalYear
): YearlyFinancials[] {
    // Sort projections by year
    const sorted = [...projections].sort((a, b) => 
        parseFiscalYear(a.year) - parseFiscalYear(b.year)
    );
    
    const changedYearNum = parseFiscalYear(changedYear);
    const startIndex = sorted.findIndex(p => parseFiscalYear(p.year) >= changedYearNum);
    
    if (startIndex === -1) return sorted;
    
    // Recalculate from the changed year forward
    const result = [...sorted];
    for (let i = startIndex; i < result.length; i++) {
        const priorYear = i > 0 ? result[i - 1] : null;
        result[i] = recalculateYear(result[i], priorYear);
    }
    
    return result;
}

/**
 * Recalculate all years in a scenario
 */
export function recalculateScenario(scenario: Scenario): Scenario {
    const sorted = [...scenario.projections].sort((a, b) => 
        parseFiscalYear(a.year) - parseFiscalYear(b.year)
    );
    
    const recalculated: YearlyFinancials[] = [];
    for (let i = 0; i < sorted.length; i++) {
        const priorYear = i > 0 ? recalculated[i - 1] : null;
        recalculated.push(recalculateYear(sorted[i], priorYear));
    }
    
    return {
        ...scenario,
        projections: recalculated,
        updatedAt: new Date().toISOString()
    };
}

/**
 * Recalculate the active scenario in a model
 */
export function recalculateModel(model: FinancialProjectionModel): FinancialProjectionModel {
    const updatedScenarios = model.scenarios.map(scenario => {
        if (scenario.id === model.activeScenarioId) {
            return recalculateScenario(scenario);
        }
        return scenario;
    });
    
    return {
        ...model,
        scenarios: updatedScenarios,
        lastUpdatedAt: new Date().toISOString()
    };
}

// ============================================================================
// CELL UPDATES
// ============================================================================

export type CellPath = 
    | { section: 'assumptions'; field: 'organicGrowthRate' | 'ebitdaMarginPercent' }
    | { section: 'revenue'; field: 'businessPlanTarget' | 'organicGrowth' | 'organicGrowthRate' | 'ebitdaMarginPercent' | 'strategyImpact' | 'grossStrategyImpact' | 'totalCapex' | 'totalOpex' | 'total' }
    | { section: 'ebitda'; field: 'businessPlanTarget' | 'organicGrowth' | 'grossStrategyImpact' | 'totalCapex' | 'totalOpex' | 'totalEbitda' };

/**
 * Update a single cell value and cascade recalculations
 */
export function updateCell(
    model: FinancialProjectionModel,
    year: FiscalYear,
    path: CellPath,
    newValue: number,
    userId?: string
): FinancialProjectionModel {
    const activeScenario = model.scenarios.find(s => s.id === model.activeScenarioId);
    if (!activeScenario) return model;
    
    const yearIndex = activeScenario.projections.findIndex(p => p.year === year);
    if (yearIndex === -1) return model;
    
    // Create updated projections
    const updatedProjections = [...activeScenario.projections];
    const currentYear = { ...updatedProjections[yearIndex] };
    
    // Update the specific cell
    const metadata = {
        source: 'user_edited' as const,
        editedAt: new Date().toISOString(),
        editedBy: userId,
        confidence: 100
    };
    
    if (path.section === 'assumptions') {
        currentYear.assumptions = {
            ...currentYear.assumptions,
            [path.field]: newValue,
            [`${path.field}Metadata`]: metadata
        };
    } else if (path.section === 'revenue') {
        currentYear.revenue = {
            ...currentYear.revenue,
            [path.field]: newValue,
            [`${path.field}Metadata`]: metadata
        };
    } else if (path.section === 'ebitda') {
        currentYear.ebitda = {
            ...currentYear.ebitda,
            [path.field]: newValue,
            [`${path.field}Metadata`]: metadata
        };
    }
    
    updatedProjections[yearIndex] = currentYear;
    
    // Cascade recalculations from this year forward
    const recalculatedProjections = recalculateFromYear(updatedProjections, year);
    
    // Update the model
    const updatedScenarios = model.scenarios.map(s => {
        if (s.id === model.activeScenarioId) {
            return {
                ...s,
                projections: recalculatedProjections,
                updatedAt: new Date().toISOString()
            };
        }
        return s;
    });
    
    return {
        ...model,
        scenarios: updatedScenarios,
        lastUpdatedAt: new Date().toISOString()
    };
}

// ============================================================================
// ADD/REMOVE YEARS
// ============================================================================

/**
 * Add a new year to the end of the projection
 */
export function addYear(model: FinancialProjectionModel): FinancialProjectionModel {
    const activeScenario = model.scenarios.find(s => s.id === model.activeScenarioId);
    if (!activeScenario) return model;
    
    const currentEndYear = parseFiscalYear(model.endYear);
    const newYear = toFiscalYear(currentEndYear + 1);
    
    // Get the last year's data to extrapolate
    const sorted = [...activeScenario.projections].sort((a, b) => 
        parseFiscalYear(a.year) - parseFiscalYear(b.year)
    );
    const lastYear = sorted[sorted.length - 1];
    
    // Create new year with decayed assumptions
    const newYearData = createEmptyYearlyFinancials(newYear, false);
    
    // Copy and slightly decay assumptions
    const decayedGrowth = Math.max(0.03, lastYear.assumptions.organicGrowthRate * 0.95);
    const improvedMargin = Math.min(0.30, lastYear.assumptions.ebitdaMarginPercent * 1.05);
    
    newYearData.assumptions = {
        organicGrowthRate: decayedGrowth,
        organicGrowthRateMetadata: createAIMetadata(70, 'Decayed from prior year'),
        ebitdaMarginPercent: improvedMargin,
        ebitdaMarginPercentMetadata: createAIMetadata(70, 'Improved from prior year')
    };
    
    // Copy initiative breakdown structure (with extended impacts)
    newYearData.revenue.initiativeBreakdown = (lastYear.revenue.initiativeBreakdown || []).map(init => ({
        ...init,
        year: newYear,
        revenueImpact: init.revenueImpact * 1.1, // 10% growth in impact
        revenueImpactMetadata: createAIMetadata(60, 'Extrapolated')
    }));
    
    newYearData.ebitda.initiativeBreakdown = (lastYear.ebitda.initiativeBreakdown || []).map(init => ({
        ...init,
        year: newYear,
        grossEbitdaImpact: init.grossEbitdaImpact * 1.1,
        grossEbitdaImpactMetadata: createAIMetadata(60, 'Extrapolated'),
        capex: 0, // CAPEX typically decreases over time
        capexMetadata: createAIMetadata(80),
        opex: init.opex * 1.03, // OPEX grows with inflation
        opexMetadata: createAIMetadata(70, 'Inflation adjusted')
    }));
    
    // Extrapolate business plan target
    const targetGrowth = lastYear.revenue.businessPlanTarget > 0 
        ? (lastYear.revenue.businessPlanTarget - sorted[sorted.length - 2]?.revenue.businessPlanTarget || 0) 
        : lastYear.revenue.total * 0.1;
    
    newYearData.revenue.businessPlanTarget = lastYear.revenue.businessPlanTarget + targetGrowth;
    newYearData.revenue.businessPlanTargetMetadata = createAIMetadata(60, 'Extrapolated from trend');
    
    newYearData.ebitda.businessPlanTarget = newYearData.revenue.businessPlanTarget * improvedMargin;
    newYearData.ebitda.businessPlanTargetMetadata = createAIMetadata(60, 'Revenue target × margin');
    
    // Add to projections and recalculate
    const updatedProjections = [...sorted, newYearData];
    const recalculated = recalculateFromYear(updatedProjections, newYear);
    
    const updatedScenarios = model.scenarios.map(s => {
        if (s.id === model.activeScenarioId) {
            return {
                ...s,
                projections: recalculated,
                updatedAt: new Date().toISOString()
            };
        }
        return s;
    });
    
    return {
        ...model,
        endYear: newYear,
        scenarios: updatedScenarios,
        lastUpdatedAt: new Date().toISOString()
    };
}

/**
 * Remove the last year from the projection
 */
export function removeYear(model: FinancialProjectionModel): FinancialProjectionModel {
    const activeScenario = model.scenarios.find(s => s.id === model.activeScenarioId);
    if (!activeScenario || activeScenario.projections.length <= 2) {
        return model; // Keep at least 2 years
    }
    
    const sorted = [...activeScenario.projections].sort((a, b) => 
        parseFiscalYear(a.year) - parseFiscalYear(b.year)
    );
    
    const updatedProjections = sorted.slice(0, -1);
    const newEndYear = updatedProjections[updatedProjections.length - 1].year;
    
    const updatedScenarios = model.scenarios.map(s => {
        if (s.id === model.activeScenarioId) {
            return {
                ...s,
                projections: updatedProjections,
                updatedAt: new Date().toISOString()
            };
        }
        return s;
    });
    
    return {
        ...model,
        endYear: newEndYear,
        scenarios: updatedScenarios,
        lastUpdatedAt: new Date().toISOString()
    };
}

// ============================================================================
// WATERFALL DATA GENERATION
// ============================================================================

export interface WaterfallBar {
    label: string;
    value: number;
    type: 'base' | 'growth' | 'strategy' | 'gap' | 'target';
    color: string;
}

/**
 * Generate waterfall chart data from a projection model
 */
export function generateWaterfallData(
    model: FinancialProjectionModel,
    metric: 'revenue' | 'ebitda' = 'revenue',
    startYear?: FiscalYear,
    endYear?: FiscalYear
): WaterfallBar[] {
    const activeScenario = model.scenarios.find(s => s.id === model.activeScenarioId);
    if (!activeScenario) return [];
    
    const sorted = [...activeScenario.projections].sort((a, b) => 
        parseFiscalYear(a.year) - parseFiscalYear(b.year)
    );
    
    const start = startYear || model.startYear;
    const end = endYear || model.endYear;
    const startNum = parseFiscalYear(start);
    const endNum = parseFiscalYear(end);
    
    const filtered = sorted.filter(p => {
        const yearNum = parseFiscalYear(p.year);
        return yearNum >= startNum && yearNum <= endNum;
    });
    
    if (filtered.length === 0) return [];
    
    const baseYear = filtered[0];
    const targetYear = filtered[filtered.length - 1];
    
    // Calculate totals
    const baseValue = metric === 'revenue' ? baseYear.revenue.total : baseYear.ebitda.totalEbitda;
    const targetValue = metric === 'revenue' ? targetYear.revenue.businessPlanTarget : targetYear.ebitda.businessPlanTarget;
    
    let totalOrganicGrowth = 0;
    let totalStrategyImpact = 0;
    
    for (const year of filtered.slice(1)) {
        if (metric === 'revenue') {
            totalOrganicGrowth += year.revenue.organicGrowth;
            totalStrategyImpact += year.revenue.strategyImpact;
        } else {
            totalOrganicGrowth += year.ebitda.organicGrowth;
            totalStrategyImpact += year.ebitda.grossStrategyImpact;
        }
    }
    
    const projectedTotal = baseValue + totalOrganicGrowth + totalStrategyImpact;
    const gap = targetValue - projectedTotal;
    
    return [
        {
            label: `Current ${metric === 'revenue' ? 'Revenue' : 'EBITDA'} (${start})`,
            value: baseValue,
            type: 'base',
            color: '#00d4aa'
        },
        {
            label: 'Projected Organic Growth',
            value: totalOrganicGrowth,
            type: 'growth',
            color: '#00d4aa'
        },
        {
            label: 'Strategy Impact',
            value: totalStrategyImpact,
            type: 'strategy',
            color: '#5d50f2'
        },
        {
            label: gap > 0 ? 'Remaining Gap' : 'Exceeded Target',
            value: Math.abs(gap),
            type: 'gap',
            color: gap > 0 ? '#ef4444' : '#22c55e'
        },
        {
            label: `Target ${metric === 'revenue' ? 'Revenue' : 'EBITDA'} (${end})`,
            value: targetValue,
            type: 'target',
            color: '#00d4aa'
        }
    ];
}
